/**
 * The signed-in identity, INCLUDING `role` — `core/identity.ts`'s
 * `currentUser()` deliberately narrows to the presence shape (`id`/`name`/
 * `color`) and dev mode never even calls `/api/me`, so nothing else on the
 * client keeps `role` around. This is the one place that needs it: gating the
 * admin-only AI model settings page (Dashboard's nav link, AdminScreen's
 * route guard).
 *
 * Works in every AUTH_MODE — `/api/me` is served either way, and in dev mode
 * resolves against whatever `x-dev-user` the display-name prompt sends, so an
 * `ADMIN_EMAILS` entry can be tested locally by typing that name in.
 */
import { useEffect, useState } from "react";

import type { MeResponse } from "@kola/shared";

import { fetchMe } from "./api.js";

/** Module-level cache: repeated mounts (e.g. navigating back) reuse one call. */
let cached: Promise<MeResponse> | null = null;

function loadMe(): Promise<MeResponse> {
  cached ??= fetchMe();
  return cached;
}

export interface UseMeResult {
  /** null while loading, or if `/api/me` failed (treated as "not signed in"). */
  me: MeResponse | null;
  loading: boolean;
}

export function useMe(): UseMeResult {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadMe()
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { me, loading };
}
